// utils/booking/bookingLocaleLinks.ts
//
// Carrying the visitor's chosen language across a link.
//
// `useBookingLocale` resolves the language from the `lang` query first, and
// falls back to the browser only when there is no `lang`. So every link the
// booking surface hands out has to SAY the language, or the destination
// silently re-resolves it from the browser: a visitor who switched the page
// to Polish and then opened the terms, or came back through a manage link,
// landed in whatever their browser prefers instead.
//
// The language is always written, English included. "Booked in English on a
// French browser" is a real state, and the link is the only thing that
// remembers it.
import { type BookingLocale, DEFAULT_BOOKING_LOCALE, isBookingLocale } from './locales'

/** The query parameter `useBookingLocale` reads. One name, one place. */
export const BOOKING_LANG_PARAM = 'lang'

/**
 * `path` with `lang` set — for an in-app link handed to NuxtLink, which takes
 * a path and not a URL. An existing `lang` is replaced, not duplicated; a
 * hash is preserved and stays last.
 */
export function withBookingLang(path: string, locale: BookingLocale): string {
  const hashAt = path.indexOf('#')
  const hash = hashAt >= 0 ? path.slice(hashAt) : ''
  const withoutHash = hashAt >= 0 ? path.slice(0, hashAt) : path
  const queryAt = withoutHash.indexOf('?')
  const base = queryAt >= 0 ? withoutHash.slice(0, queryAt) : withoutHash
  const params = new URLSearchParams(queryAt >= 0 ? withoutHash.slice(queryAt + 1) : '')
  params.set(BOOKING_LANG_PARAM, locale)
  return `${base}?${params.toString()}${hash}`
}

/**
 * An absolute URL with `lang` set — for the `manage_url` the API mints, so
 * the manage page opens in the language the booking was made in.
 *
 * A value that is not a URL is returned untouched: this decorates a link the
 * server issued, and inventing one when the server sent something unusable
 * would turn a missing link into a broken one.
 */
export function withBookingLangUrl(url: string, locale: BookingLocale): string {
  const trimmed = (url ?? '').trim()
  if (!trimmed) return ''
  try {
    const parsed = new URL(trimmed)
    parsed.searchParams.set(BOOKING_LANG_PARAM, locale)
    return parsed.toString()
  }
  catch {
    return trimmed
  }
}

/**
 * The locale a `lang` query names, for a page that renders in the visitor's
 * language without running the whole booking resolver — the terms and privacy
 * pages, which have no page default of their own. Anything unrecognised is
 * English, exactly as `resolveBookingLocale` treats it.
 */
export function bookingLangFromQuery(value: unknown): BookingLocale {
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== 'string') return DEFAULT_BOOKING_LOCALE
  const trimmed = raw.trim()
  return isBookingLocale(trimmed) ? trimmed : DEFAULT_BOOKING_LOCALE
}
